import { Injectable } from '@angular/core';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { BehaviorSubject, Observable, race } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
    CreateCourseOffering,
    CreateCourseOfferingFailure,
    CreateCourseOfferingSuccess,
    CreateProgramOffering,
    CreateProgramOfferingFailure,
    CreateProgramOfferingSuccess,
    DeleteCourseOffering,
    DeleteCourseOfferingFailure,
    DeleteCourseOfferingSuccess,
    FetchAssignedCertificates,
    FetchAssignedCertificatesFailure,
    FetchAssignedCertificatesSuccess,
    FetchCourseOfferingsByCategory,
    FetchCourseOfferingsByCategoryFailure,
    FetchCourseOfferingsByCategorySuccess,
    LinkAndUnlinkAudiences,
    LinkAndUnlinkAudiencesFailure,
    LinkAndUnlinkAudiencesSuccess,
    SearchCourseOfferingsByCategory,
    SearchCourseOfferingsByCategoryFailure,
    SearchCourseOfferingsByCategorySuccess,
    UpdateOffering,
    UpdateOfferingCategory,
    UpdateOfferingCategoryFailure,
    UpdateOfferingCategorySuccess,
    UpdateOfferingFailure,
    UpdateOfferingOrder,
    UpdateOfferingOrderFailure,
    UpdateOfferingOrderSuccess,
    UpdateOfferingSuccess,
    UpdateProgramOfferingCompletion,
    UpdateProgramOfferingCompletionFailure,
    UpdateProgramOfferingCompletionSuccess,
} from '../actions';
import { OfferingType } from '../enums/offering-type.enum';
import { IChangeCourseOfferingCategoryActionPayload } from '../interfaces/change-course-offering-category.action.payload.interface';
import { ICreateOfferingActionPayload } from '../interfaces/create-offering.action.payload.interface';
import { ICreateProgramOfferingActionPayload } from '../interfaces/create-program-offering.action.payload.interface';
import { CourseOfferingsByCategory } from '../models/course-offering-by-group.model';
import { CourseOfferingWithBreadcrumb } from '../models/course-offering-with-breadcrumb.model';
import { IDeleteCourseOfferingResult } from '../interfaces/delete-course-offering-result.interface';
import { CourseOffering } from '../models/course-offering.model';
import { CourseOfferingState } from '../models/course-offering.state';

const COURSE_OFFERING_PARENT_URL_KEY = 'courseOfferingParentUrl';

@Injectable()
export class CourseOfferingService {
    @Select(CourseOfferingState.getEntities)
    courseOffering$: Observable<CourseOffering[]>;

    @Select(CourseOfferingState.getEntity)
    currentCourseOffering$: Observable<CourseOffering>;

    private _courseOfferingsByCategorySubject = new BehaviorSubject<CourseOfferingsByCategory[]>(null);
    public courseOfferingsByCategorySubject$ = this._courseOfferingsByCategorySubject.asObservable();

    private _filtersList = new BehaviorSubject<IChoosenFilterInterface[]>([]);
    public filtersList$ = this._filtersList.asObservable();

    private courseOfferingPath = '';

    getParentUrl(): string {
        // sessionStorage keeps parent URL in sync across lazy-module injectors
        // (CourseOfferingModule providers are not a true app-wide singleton).
        const stored = sessionStorage.getItem(COURSE_OFFERING_PARENT_URL_KEY);
        if (stored !== null) {
            this.courseOfferingPath = stored;
            return stored;
        }

        this.courseOfferingPath = '';
        return '';
    }

    setParentUrl(path: string): void {
        this.courseOfferingPath = path || '';

        if (this.courseOfferingPath) {
            sessionStorage.setItem(COURSE_OFFERING_PARENT_URL_KEY, this.courseOfferingPath);
        } else {
            sessionStorage.removeItem(COURSE_OFFERING_PARENT_URL_KEY);
        }
    }

    constructor(private _store: Store, private _actionHelper: NgxsActionHelper, private _action$: Actions) {}

    setFiltersFromStorage(): void {
        this._filtersList.next(this.getFiltersFromStorage());
    }

    getFiltersFromStorage(): IChoosenFilterInterface[] {
        const raw = sessionStorage.getItem('bookingsFiltersList');

        if (!raw) {
            return [];
        }

        try {
            return JSON.parse(raw) || [];
        } catch {
            return [];
        }
    }

    setFiltersList(value: IChoosenFilterInterface[], toEditSessionStorage: boolean = false): void {
        this._filtersList.next(value);

        if (toEditSessionStorage) {
            sessionStorage.setItem('bookingsFiltersList', JSON.stringify(value));
        }
    }

    getCourseOfferingById(courseOfferingId: string): CourseOffering {
        return CourseOfferingState.getEntity(this._store.selectSnapshot(CourseOfferingState), courseOfferingId);
    }

    async searchCourseOfferingsByCategory(
        categoryId: string,
        searchTerm?: string,
        tags?: string[],
        types?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        columns?: string[]
    ): Promise<CourseOfferingWithBreadcrumb> {
        this._store.dispatch(new SearchCourseOfferingsByCategory({ categoryId, searchTerm, tags, types, extendedAttributesFilters, columns }));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(SearchCourseOfferingsByCategorySuccess),
                map((action: SearchCourseOfferingsByCategorySuccess) => {
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(SearchCourseOfferingsByCategoryFailure),
                map((action: SearchCourseOfferingsByCategoryFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchCourseOfferingsByCategory(
        categoryId: string,
        searchTerm?: string,
        courseOfferingType?: OfferingType,
        tags?: string[],
        types?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[]
    ): Promise<CourseOfferingsByCategory[]> {
        this._store.dispatch(new FetchCourseOfferingsByCategory({ categoryId, searchTerm, courseOfferingType, tags, types, extendedAttributesFilters }));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(FetchCourseOfferingsByCategorySuccess),
                map((action: FetchCourseOfferingsByCategorySuccess) => {
                    this._courseOfferingsByCategorySubject.next(action.payload);
                    return action.payload;
                })
            ),
            this._action$.pipe(
                ofActionDispatched(FetchCourseOfferingsByCategoryFailure),
                map((action: FetchCourseOfferingsByCategoryFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async createCourseOffering(courseOffering: ICreateOfferingActionPayload): Promise<boolean> {
        this._store.dispatch(new CreateCourseOffering(courseOffering));

        return await this._actionHelper.race(CreateCourseOfferingSuccess, CreateCourseOfferingFailure).toPromise();
    }

    async createProgramOffering(courseOffering: ICreateProgramOfferingActionPayload): Promise<boolean> {
        this._store.dispatch(new CreateProgramOffering(courseOffering));

        return await this._actionHelper.race(CreateProgramOfferingSuccess, CreateProgramOfferingFailure).toPromise();
    }

    async updateOffering(offering: CourseOffering): Promise<boolean> {
        this._store.dispatch(new UpdateOffering(offering));

        return await this._actionHelper.race(UpdateOfferingSuccess, UpdateOfferingFailure).toPromise();
    }

    async updateOfferingOrder(offering: CourseOffering): Promise<boolean> {
        this._store.dispatch(new UpdateOfferingOrder(offering));

        return await this._actionHelper.race(UpdateOfferingOrderSuccess, UpdateOfferingOrderFailure).toPromise();
    }

    async updateProgramOfferingCompletion(offering: CourseOffering): Promise<boolean> {
        this._store.dispatch(new UpdateProgramOfferingCompletion(offering));

        return await this._actionHelper.race(UpdateProgramOfferingCompletionSuccess, UpdateProgramOfferingCompletionFailure).toPromise();
    }

    async updateOfferingCategory(payload: IChangeCourseOfferingCategoryActionPayload): Promise<boolean> {
        this._store.dispatch(new UpdateOfferingCategory(payload));

        return await this._actionHelper.race(UpdateOfferingCategorySuccess, UpdateOfferingCategoryFailure).toPromise();
    }

    async deleteCourseOffering(courseOfferingId: string): Promise<IDeleteCourseOfferingResult | null> {
        this._store.dispatch(new DeleteCourseOffering(courseOfferingId));

        return race(
            this._action$.pipe(
                ofActionDispatched(DeleteCourseOfferingSuccess),
                map((action: DeleteCourseOfferingSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(DeleteCourseOfferingFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchAssignedCertificates(courseOfferingId: string): Promise<boolean> {
        this._store.dispatch(new FetchAssignedCertificates({ courseOfferingId: courseOfferingId }));
        return this._actionHelper.race(FetchAssignedCertificatesSuccess, FetchAssignedCertificatesFailure).toPromise();
    }

    async linkAndUnlinkAudiences(courseOfferingId: string, linkedAudiences: { id: string }[], removeUsers?: boolean): Promise<CourseOffering> {
        this._store.dispatch(new LinkAndUnlinkAudiences({ courseOfferingId, linkedAudiences, removeUsers }));

        return race(
            this._action$.pipe(
                filter((courseOfferings) => !!courseOfferings),
                ofActionDispatched(LinkAndUnlinkAudiencesSuccess),
                map((action: LinkAndUnlinkAudiencesSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(LinkAndUnlinkAudiencesFailure),
                map((action: LinkAndUnlinkAudiencesFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }
}
